/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { Component } from "react";
import localizeComponent from "dc-preview-utils/src/Utils/LocalizationUtil";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import loadTranslation, { getMessage } from "utils/TranslationUtil";
import { actionTags, rhpDropinIds } from "constants/enums";
import { getDigSigRHPVisibility } from "selectors/digSigSelectors";
import * as stores from "stores";
import DigSigRHPIconContainer from "containers/digsig/DigSigRHPIconContainer";
import { trackDigSigPanelOpen } from "analytics/events";

class DigSigBootstrap extends Component {
  constructor() {
    super();
    this.shouldShowExpandedComponent = false;
    this.localizedComponent = null;
  }

  getLabel = () => getMessage("label.digsig");

  getIcon = () => () => <DigSigRHPIconContainer />;

  getIconStyle = () => ({
    SELECTED: {
      LIGHT_MODE: {
        color: "var(--spectrum-global-color-gray-50)",
        background: "#464646",
        useCustomFontColor: true,
        isMultiColorIcon: true
      },
      DARK_MODE: {
        color: "var(--spectrum-global-color-gray-50)",
        background: "#d1d1d1",
        useCustomFontColor: true,
        isMultiColorIcon: true
      }
    }
  });

  getTooltip = () => getMessage("modernviewer.tooltip.digsig");

  getAriaLabel = () => getMessage("modernviewer.arialabel.digsig");

  getActionTags = () => [actionTags.LOCAL];

  getFileSelectionParams = () => ({
    acceptMimeTypes: ["application/pdf"],
    acceptExtensions: ["pdf"],
    acceptConnectors: "all"
  });

  id = rhpDropinIds.DIGITAL_SIGNATURES;

  initializeAction = opts => {
    if (opts) stores.digSigStore.setInitializeOpts(opts, this._loadDigSigContainer);
    stores.digSigStore.initializeAction();
  };

  _loadDigSigContainer = () =>
    new Promise((resolve, reject) => {
      if (this.localizedComponent) {
        return resolve(this.localizedComponent);
      }

      import(
        /* webpackChunkName: "digital-signatures" */
        "containers/digsig/DigSigRHPContainer"
      )
        .then(module => {
          const digsigRHPContainer = module.default;
          this.localizedComponent = localizeComponent(digsigRHPContainer, loadTranslation);
          resolve(this.localizedComponent);
        })
        .catch(() => reject(null));
    });

  getExpandedComponent = () =>
    new Promise((resolve, reject) => {
      if (!stores.digSigStore.shouldShowExpandedComponent) {
        return reject(null);
      }

      this._loadDigSigContainer()
        .then(component => {
          trackDigSigPanelOpen();
          resolve(component);
        })
        .catch(() => reject(null));
    });

  render() {
    return null;
  }
}

export default DigSigBootstrap;
